import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';
import { useSelector } from 'react-redux';
import isNil from 'lodash/isNil';
import { useRouteParams } from '../../../hooks/useRouteParams';
import { useFinancialPlanQuery } from '../../../../api/useFinancialPlanQuery';
import { PlanActionStepDTO } from '../../../../types/planActionStepsDTO';
import { AsyncComponentHookResult } from '../../../../../Types/ComponentHookResult';
import { selectedGoalSelector } from '../../../../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { ActionStepType } from '../../../../types/actionStepTypeOptionDTO';
import { GoalDTO } from '../../../../types/goalsDTO';

export const useSuggestions = (
  _goalId?: string,
): AsyncComponentHookResult<{ suggestions: PlanActionStepDTO[]; goal: GoalDTO }> => {
  const { clientId, goalId: routeGoalId } = useRouteParams();

  const goalId = _goalId ?? routeGoalId;

  const queryResult = useFinancialPlanQuery();

  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const selectedGoal = useSelector(state => selectedGoalSelector(state, { clientId }));

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const { PlanActionSteps } = queryResult.data;

    if (!PlanActionSteps || !selectedGoal) {
      return null;
    }

    const goalActionSteps = PlanActionSteps.filter(({ GoalId }) => GoalId === goalId);

    const goalSuggestions = goalActionSteps.filter(actionStep => {
      if (
        actionStep.Type !== ActionStepType.Suggestion &&
        actionStep.Type !== ActionStepType.AdditionalTips
      ) {
        return false;
      }

      if (selectedGoal.isSubGoal) {
        return actionStep.DebtType === selectedGoal.DebtType;
      }

      // Don't render sub goals suggestion on the parent goal
      return isNil(actionStep.DebtType);
    });

    return {
      suggestions: sortBy(goalSuggestions, item => new Date(item.CreatedOn), 'desc'),
      goal: selectedGoal,
    };
  }, [goalId, queryResult.data, selectedGoal]);

  return {
    ...queryResult,
    data,
  };
};
